import { Group, Select } from '@byecode/ui'
import type { FlowNode, NodeTypes, VariableTree2, WechatNavigateToMiniProgramActionConfig } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import * as SC from '../../styles'

interface WechatNavigateToMiniProgramActionConfigureProps {
    allParentNodes?: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    nodeType?: NodeTypes
    value: WechatNavigateToMiniProgramActionConfig
    onChange: (value: WechatNavigateToMiniProgramActionConfig) => void
}

export const WechatNavigateToMiniProgramActionConfigure: React.FC<WechatNavigateToMiniProgramActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const { viewDsId = '', formDsId = '', viewType } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : undefined,
        [allParentNodes, dataSourceList, isInFlow, workflowList]
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const { customViewOption } = useVariableCustomViewOption()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }

        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const userOption = useGetUserRecordOptions()

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>小程序id</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            autoHeight
                            value={value.appId}
                            onBlur={appId => onChange({ ...value, appId })}
                            placeholder="请输入"
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>页面路径</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            autoHeight
                            value={value.path}
                            onBlur={path => onChange({ ...value, path })}
                            placeholder="请输入"
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
